import { useEffect, useState } from "react";
import { Loader2, Plus, Pencil, Trash2, ArrowLeft } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const POSITIONS = [
  "Accounts / Office Assistant", "Sales Executive", "Sales Manager", "Security",
  "Computer / Laptop Technician", "Networking Technician", "Printer / Service Technician",
  "CCTV Technician", "Other",
];
const STATUSES = ["OPEN", "CLOSED"];

const STATUS_STYLES = {
  OPEN: "bg-green-100 text-green-800",
  CLOSED: "bg-cts-soft text-cts-charcoal/60",
};

const emptyForm = {
  position: POSITIONS[0], custom_title: "", location: "", employment_type: "",
  experience_required: "", qualifications: "", job_description: "", responsibilities: "",
  how_to_apply: "", closing_date: "", status: "OPEN",
};

const VacancyForm = ({ vacancy, onSaved, onCancel }) => {
  const [form, setForm] = useState(vacancy || emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      if (vacancy?.id) {
        await api.put(`/admin/vacancies/${vacancy.id}`, form);
      } else {
        await api.post("/admin/vacancies", form);
      }
      onSaved();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not save vacancy");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="rounded-xl border border-cts-line bg-white p-6 space-y-5" data-testid="vacancy-form">
      <button type="button" onClick={onCancel} data-testid="vacancy-form-back-btn" className="flex items-center gap-1.5 text-sm font-semibold text-cts-charcoal hover:text-cts-red">
        <ArrowLeft size={15} /> Back to Vacancies
      </button>

      {error && <p className="rounded-md bg-cts-redlight px-4 py-3 text-sm text-cts-ink" role="alert" data-testid="vacancy-form-error">{error}</p>}

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="field-label">Position *</label>
          <select required data-testid="vacancy-position-select" className="field-input" value={form.position} onChange={(e) => set("position", e.target.value)}>
            {POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        {form.position === "Other" && (
          <div>
            <label className="field-label">Custom Job Title *</label>
            <input required data-testid="vacancy-custom-title-input" className="field-input" maxLength={150} value={form.custom_title || ""} onChange={(e) => set("custom_title", e.target.value)} />
          </div>
        )}
        <div>
          <label className="field-label">Location</label>
          <input data-testid="vacancy-location-input" className="field-input" maxLength={200} value={form.location || ""} onChange={(e) => set("location", e.target.value)} />
        </div>
        <div>
          <label className="field-label">Employment Type</label>
          <input data-testid="vacancy-employment-type-input" className="field-input" maxLength={100} placeholder="Full-time, Part-time..." value={form.employment_type || ""} onChange={(e) => set("employment_type", e.target.value)} />
        </div>
        <div>
          <label className="field-label">Experience Required</label>
          <input data-testid="vacancy-experience-input" className="field-input" maxLength={200} value={form.experience_required || ""} onChange={(e) => set("experience_required", e.target.value)} />
        </div>
        <div>
          <label className="field-label">Application Closing Date</label>
          <input type="date" data-testid="vacancy-closing-date-input" className="field-input" value={form.closing_date || ""} onChange={(e) => set("closing_date", e.target.value)} />
        </div>
        <div className="sm:col-span-2">
          <label className="field-label">Qualifications / Skills</label>
          <textarea data-testid="vacancy-qualifications-input" className="field-input" rows={2} maxLength={2000} value={form.qualifications || ""} onChange={(e) => set("qualifications", e.target.value)} />
        </div>
        <div className="sm:col-span-2">
          <label className="field-label">Job Description</label>
          <textarea data-testid="vacancy-job-description-input" className="field-input" rows={3} maxLength={3000} value={form.job_description || ""} onChange={(e) => set("job_description", e.target.value)} />
        </div>
        <div className="sm:col-span-2">
          <label className="field-label">Key Responsibilities</label>
          <textarea data-testid="vacancy-responsibilities-input" className="field-input" rows={3} maxLength={3000} value={form.responsibilities || ""} onChange={(e) => set("responsibilities", e.target.value)} />
        </div>
        <div className="sm:col-span-2">
          <label className="field-label">How to Apply</label>
          <textarea data-testid="vacancy-how-to-apply-input" className="field-input" rows={2} maxLength={1000} value={form.how_to_apply || ""} onChange={(e) => set("how_to_apply", e.target.value)} />
        </div>
        <div>
          <label className="field-label">Status</label>
          <select data-testid="vacancy-status-select" className="field-input" value={form.status} onChange={(e) => set("status", e.target.value)}>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      <button type="submit" disabled={saving} data-testid="vacancy-save-btn" className="btn-primary disabled:opacity-60">
        {saving ? <Loader2 className="animate-spin" size={16} /> : "SAVE VACANCY"}
      </button>
    </form>
  );
};

export const VacanciesTab = () => {
  const [vacancies, setVacancies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/vacancies");
      setVacancies(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const displayTitle = (v) => (v.position === "Other" && v.custom_title ? v.custom_title : v.position);

  const onDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    await api.delete(`/admin/vacancies/${id}`);
    load();
  };

  const toggleStatus = async (v) => {
    await api.put(`/admin/vacancies/${v.id}`, { ...v, status: v.status === "OPEN" ? "CLOSED" : "OPEN" });
    load();
  };

  if (editing !== null) {
    return (
      <VacancyForm
        vacancy={editing.id ? editing : null}
        onSaved={() => { setEditing(null); load(); }}
        onCancel={() => setEditing(null)}
      />
    );
  }

  return (
    <div data-testid="admin-vacancies-tab">
      <div className="flex justify-end mb-4">
        <button onClick={() => setEditing({})} data-testid="vacancy-new-btn" className="btn-primary">
          <Plus size={16} /> NEW VACANCY
        </button>
      </div>
      {loading ? (
        <div className="text-center py-10"><Loader2 className="animate-spin mx-auto text-cts-red" size={24} /></div>
      ) : vacancies.length === 0 ? (
        <div className="rounded-xl border border-cts-line bg-white p-14 text-center" data-testid="admin-vacancies-empty">
          <p className="font-heading font-bold text-cts-ink">No vacancies yet</p>
          <p className="text-sm text-cts-charcoal mt-1">Create your first job vacancy.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-cts-line bg-white" data-testid="admin-vacancies-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-cts-line text-left">
                {["Position", "Location", "Status", "Updated", ""].map((h) => (
                  <th key={h} className="px-4 py-3 font-heading font-bold text-xs uppercase tracking-wide text-cts-charcoal">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {vacancies.map((v) => (
                <tr key={v.id} className="border-b border-cts-line last:border-0" data-testid={`vacancy-row-${v.id}`}>
                  <td className="px-4 py-3 font-semibold text-cts-ink">{displayTitle(v)}</td>
                  <td className="px-4 py-3 text-cts-charcoal">{v.location || "—"}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => toggleStatus(v)} data-testid={`vacancy-status-toggle-${v.id}`} className={`rounded px-2 py-1 text-xs font-bold ${STATUS_STYLES[v.status]}`}>
                      {v.status}
                    </button>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-cts-charcoal">{new Date(v.updated_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</td>
                  <td className="px-4 py-3 flex items-center gap-3">
                    <button onClick={() => setEditing(v)} data-testid={`vacancy-edit-${v.id}`} className="text-cts-charcoal hover:text-cts-red" aria-label={`Edit ${displayTitle(v)}`}><Pencil size={16} /></button>
                    <button onClick={() => onDelete(v.id, displayTitle(v))} data-testid={`vacancy-delete-${v.id}`} className="text-cts-charcoal/60 hover:text-cts-red" aria-label={`Delete ${displayTitle(v)}`}><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
